// LiveWaveform — real-time mic amplitude bars for the recording state.
//
// Reads RMS amplitude from the recorder's AnalyserNode on every animation
// frame and scrolls it through a fixed-width bar buffer, drawn on a <canvas>.
// The rAF loop mutates the canvas directly — no Preact re-render per frame.

import type { JSX } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { T } from '../../tokens';

const BARS = 38;
const HEIGHT = 44;

export interface LiveWaveformProps {
  readonly analyser: AnalyserNode | null;
}

export function LiveWaveform({ analyser }: LiveWaveformProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const levelsRef = useRef<number[]>(new Array<number>(BARS).fill(0));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return;

    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    const resize = (): void => {
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
      canvas.height = Math.round(HEIGHT * dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    const buf = analyser !== null ? new Uint8Array(analyser.fftSize) : null;
    let raf = 0;

    const tick = (): void => {
      const levels = levelsRef.current;
      if (analyser !== null && buf !== null) {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const x = ((buf[i] ?? 128) - 128) / 128;
          sum += x * x;
        }
        const rms = Math.sqrt(sum / buf.length);
        levels.push(Math.min(1, rms * 2.6));
      } else {
        levels.push(0);
      }
      if (levels.length > BARS) levels.shift();

      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const gap = 3 * dpr;
      const barW = Math.max(1, (w - gap * (BARS - 1)) / BARS);
      ctx.fillStyle = T.brand;
      for (let i = 0; i < levels.length; i += 1) {
        const lvl = levels[i] ?? 0;
        const barH = Math.max(2 * dpr, lvl * h);
        const x = i * (barW + gap);
        const y = (h - barH) / 2;
        ctx.fillRect(x, y, barW, barH);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [analyser]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: HEIGHT, display: 'block' }} />;
}
